import React, { useMemo } from 'react';
import {
  convertFromIC,
  convertToIC,
  Option,
  QueryBuilder,
  RuleGroupType,
  RuleGroupTypeIC,
} from 'react-querybuilder';
import 'react-querybuilder/dist/query-builder.scss';
import './styles.scss';
import { CustomCheckBox } from 'components/input';
import {
  useHasIndependentConstraints,
  useIsSelectedSearchTargetScreenHits,
  useSearchConstraints,
  useSearchTargetRelations,
  useSelectedSearchTarget,
  useSetSearchConstraints,
} from 'redux/hooks/searchnFilter';
import { SearchFieldDTO, SearchTargetDTO } from 'business/models/Models';
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import * as ReactDnD from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import {
  addGroupAction,
  addRuleAction,
  combinatorSelector,
  removeGroupAction,
  removeRuleAction,
  classTypeMappingToInputTypeMapping,
  mappedConstraintsAllowedOperators,
  ExtendedValueEditor,
} from 'components/querybuilder';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { useModalHandlers } from 'components/modal';
import ScreenHitScoreSelectionModal from './screenHitScoreSelectionModal';

const flattenFieldsList: { [key: number]: Option } = {};

const SearchConstraints = (props) => {
  const targetRelations: { [key: string]: SearchTargetDTO[] } =
    useSearchTargetRelations();
  const selectedSearchTargetId: number = useSelectedSearchTarget();
  const constraintsQuery: RuleGroupType = useSearchConstraints();
  const setConstraints = useSetSearchConstraints();
  const { showModal, hideModal } = useModalHandlers();
  const isSelectedSearchTargetScreenHits = useIsSelectedSearchTargetScreenHits();
  const [hasIndependentConstraints, toggleHasIndependentConstraints] =
    useHasIndependentConstraints();
  const targetsRelationsOptionsList = useMemo(() => {
    if (!selectedSearchTargetId || !targetRelations[selectedSearchTargetId]) {
      return [];
    }

    return targetRelations[selectedSearchTargetId]
      .filter((st) => st.searchFields.length)
      .map((st: SearchTargetDTO) => {
        const optionsList: Option[] = st.searchFields.map(
          (sf: SearchFieldDTO): Option => {
            const field = {
              name: `${sf.id}`,
              label: sf.fieldAndTableDisplayName,
              selectionLabel: sf.fieldAndTableDisplayName,
              datatype: sf.fieldClass,
              inputType: classTypeMappingToInputTypeMapping[sf.fieldClass],
            };
            flattenFieldsList[sf.id] = field;

            return field;
          },
        );

        return { label: st.name, options: optionsList };
      });
  }, [targetRelations, selectedSearchTargetId]);

  const handleOnQueryChange = (query: RuleGroupType) => {
    setConstraints(query);
  };

  const handleGetOperators = (field: string) => {
    const currentFieldObject = flattenFieldsList[field];
    if (!currentFieldObject) {
      return null;
    }

    return (
      mappedConstraintsAllowedOperators[currentFieldObject.datatype] ||
      mappedConstraintsAllowedOperators['String']
    );
  };

  const handleOnClickScreenHitsScore = () => {
    showModal({
      heading: 'Screen Hits Score',
      body: <ScreenHitScoreSelectionModal hideModal={hideModal} />,
      scrollable: false,
      showButtons: false,
    });
  };

  const handleICToggle = () => {
    toggleHasIndependentConstraints();
    if (constraintsQuery.combinator) {
      //@ts-ignore
      setConstraints(convertToIC(constraintsQuery));
    } else {
      setConstraints(convertFromIC(constraintsQuery as RuleGroupTypeIC));
    }
  };

  return (
    <>
      <div
        id="searchconstraints-search-query-wrapper"
        className="flex-row flex justify-between items-center h-12"
      >
        <span id="searchconstraints-search-query-text" className="font-bold">
          Search Query
        </span>
        <div
          id="searchconstraints-search-query-buttons-wrapper"
          className="flex items-center"
        >
          {isSelectedSearchTargetScreenHits && (
            <RMCustomButton
              title="SCREEN HITS SCORE"
              contentContainerClass="bg-black text-white p-2 w-full"
              mainContainerClass="mr-4"
              isDisabled={false}
              onClick={handleOnClickScreenHitsScore}
            />
          )}

          <CustomCheckBox
            label={'Independent combinators'}
            onChange={handleICToggle}
            id={'cb-indepant-comb'}
            checkboxIconClass="cursor-pointer"
            checked={hasIndependentConstraints}
            disabled={false}
          />
        </div>
      </div>
      <div
        id="searchconstraints-query-builder-wrapper"
        className="w-full rounded-sm border border-gray-500 h-full overflow-scroll"
      >
        <div id="searchconstraints-query-builder-container" className="p-1 flex w-full">
          <QueryBuilderDnD dnd={{ ...ReactDnD, ...ReactDndHtml5Backend }}>
            <QueryBuilder
              getOperators={handleGetOperators}
              fields={targetsRelationsOptionsList}
              query={constraintsQuery}
              resetOnOperatorChange={false}
              resetOnFieldChange
              onQueryChange={handleOnQueryChange}
              controlClassnames={{
                ruleGroup: 'ruleGroup',
                fields: 'editorField',
                operators: 'operators-selector',
                value: 'editorField value-editor-control',
              }}
              controlElements={{
                combinatorSelector: combinatorSelector,
                addRuleAction: addRuleAction,
                addGroupAction: addGroupAction,
                removeGroupAction: removeGroupAction,
                removeRuleAction: removeRuleAction,
                valueEditor: ExtendedValueEditor,
              }}
            />
          </QueryBuilderDnD>
        </div>
      </div>
    </>
  );
};

export default SearchConstraints;
